import { Suspense, lazy, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { LogOut, Shield, Coins, Bug, Loader2, Menu } from 'lucide-react';
import logo from '@/assets/logo.png';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

const PaymentHistory = lazy(() => import('./PaymentHistory').then(m => ({ default: m.PaymentHistory })));

interface HeaderProps {
  onUpgrade: () => void;
  isUpgrading?: boolean;
}

export function Header({ onUpgrade, isUpgrading = false }: HeaderProps) {
  const { user, profile, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    await signOut();
    navigate('/');
  };

  return (
    <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src={logo} alt="Scalemage logo" className="h-8 w-8" />
          <span className="text-xl font-bold tracking-tight">Scalemage</span>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <>
              {/* Desktop Layout */}
              {!isMobile && (
                <>
                  <Suspense fallback={<Skeleton className="h-8 w-8 rounded-md" />}>
                    <PaymentHistory />
                  </Suspense>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                      >
                        <a href="mailto:noster91@gmail.com?subject=Scalemage Issue Report">
                          <Bug className="h-4 w-4" />
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Report an issue</TooltipContent>
                  </Tooltip>

                  <div className="flex items-center gap-2 bg-secondary px-3 py-1.5 rounded-full">
                    <Coins className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      {profile?.credits ?? 0} Credits
                    </span>
                  </div>
                  
                  <Button
                    onClick={onUpgrade}
                    disabled={isUpgrading}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  >
                    {isUpgrading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'UPGRADE'
                    )}
                  </Button>

                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => navigate('/admin')}
                      title="Admin Dashboard"
                    >
                      <Shield className="h-4 w-4" />
                    </Button>
                  )}

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {profile?.email}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSignOut}
                      title="Sign out"
                    >
                      <LogOut className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}

              {/* Mobile Layout */}
              {isMobile && (
                <>
                  <Button
                    onClick={onUpgrade}
                    disabled={isUpgrading}
                    size="sm"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                  >
                    {isUpgrading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'UPGRADE'
                    )}
                  </Button>

                  <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <Menu className="h-5 w-5" />
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-72">
                      <SheetHeader>
                        <SheetTitle>Menu</SheetTitle>
                      </SheetHeader>
                      <div className="flex flex-col gap-4 mt-6">
                        <div className="flex items-center gap-2 bg-secondary px-3 py-2 rounded-lg">
                          <Coins className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">
                            {profile?.credits ?? 0} Credits
                          </span>
                        </div>

                        <Suspense fallback={<Skeleton className="h-10 w-full rounded-md" />}>
                          <PaymentHistory />
                        </Suspense>

                        <Button
                          variant="ghost"
                          className="justify-start"
                          asChild
                        >
                          <a href="mailto:noster91@gmail.com?subject=Scalemage Issue Report">
                            <Bug className="h-4 w-4 mr-2" />
                            Report an issue
                          </a>
                        </Button>

                        {isAdmin && (
                          <Button
                            variant="ghost"
                            className="justify-start"
                            onClick={() => {
                              setMobileMenuOpen(false);
                              navigate('/admin');
                            }}
                          >
                            <Shield className="h-4 w-4 mr-2" />
                            Admin Dashboard
                          </Button>
                        )}

                        <div className="border-t border-border pt-4 mt-2">
                          <p className="text-sm text-muted-foreground mb-3 px-1">
                            {profile?.email}
                          </p>
                          <Button
                            variant="ghost"
                            className="justify-start w-full"
                            onClick={handleSignOut}
                          >
                            <LogOut className="h-4 w-4 mr-2" />
                            Sign out
                          </Button>
                        </div>
                      </div>
                    </SheetContent>
                  </Sheet>
                </>
              )}
            </>
          ) : (
            <Button onClick={() => navigate('/auth')}>
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
